import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "react-query";
import { LuCircleUserRound, LuLogOut, LuSearch, LuX, LuPlus, LuChevronDown, LuBuilding2 } from "react-icons/lu";
import { COMPANY_ID, ROLE } from "../../config";
import { requestError, setDefaultCompany } from "../../api/idom";
import { useUserCompanies } from "../../hooks/useUserCompanies";
import { useAppBrandInfo } from "../../hooks/useAppBrandInfo";
import { createCompanyUrl, switchCompany } from "../../utils/company";
import styles from "./ProfileDropdown.module.css";

function Spinner({ label = "Loading companies" }) {
  return <div className={styles.loading} role="status" aria-label={label}>
    <span className={styles.spinner} />
  </div>;
}

function CompanyPicker({ companyQuery, onClose }) {
  const { companies, selectedCompany, defaultCompany, isSameSelectedAndDefault } = companyQuery;
  const isAdmin = Cookies.get(ROLE) === "COMPANY_ADMIN";
  const [expanded, setExpanded] = useState(!Cookies.get(COMPANY_ID));
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const queryClient = useQueryClient();
  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const searchQuery = useUserCompanies({ search, enabled: Boolean(search) });
  const activeQuery = search ? searchQuery : companyQuery;
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = activeQuery;
  useEffect(() => {
    if (!expanded || !hasNextPage || isFetchingNextPage || isError || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchNextPage();
    }, { root: listRef.current, threshold: 0.1 });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [expanded, hasNextPage, isFetchingNextPage, fetchNextPage, isError, search]);

  const mutation = useMutation(setDefaultCompany, {
    onMutate: () => setMessage(""),
    onSuccess: (response) => {
      setMessage(response?.message || "Default company updated successfully");
      queryClient.invalidateQueries(["user-companies"]);
    },
  });
  const canExpand = companies.length > 0 || (!isSameSelectedAndDefault && Boolean(defaultCompany)) ||
    isAdmin || companyQuery.isLoading || companyQuery.isError;
  const otherCompanies = companies.filter((company) =>
    String(company.company_id) !== String(selectedCompany?.company_id) &&
    String(company.company_id) !== String(defaultCompany?.company_id)
  );
  const orderedCompanies = [
    ...(!isSameSelectedAndDefault && defaultCompany ? [defaultCompany] : []),
    ...otherCompanies,
  ];

  const defaultControl = (company, isDefault) => isDefault
    ? <span className={styles.badge}>Default</span>
    : company && <button type="button" className={styles.setDefault}
        disabled={mutation.isLoading}
        aria-label={`Set ${company.company_name} as default`}
        onClick={() => mutation.mutate(company.company_id)}>
        {mutation.isLoading && String(mutation.variables) === String(company.company_id) ? "Saving..." : "Set Default"}
      </button>;

  const companyRow = (company) => {
    const isDefault = String(company.company_id) === String(defaultCompany?.company_id) || company.default_company === true;
    const isSelected = String(company.company_id) === String(selectedCompany?.company_id);
    return <div className={styles.companyRow} key={company.company_id}>
      <button type="button" className={styles.companyChoice}
        aria-label={`Switch to ${company.company_name}`} aria-current={isSelected ? "true" : undefined}
        onClick={() => switchCompany(company)}>
        <span className={styles.companyName} title={company.company_name}>{company.company_name}</span>
        {(company.industry || company.city) && <span className={styles.companyDetail}>
          {[company.industry, company.city].filter(Boolean).join(" • ")}
        </span>}
      </button>
      {defaultControl(company, isDefault)}
    </div>;
  };

  return <>
    <div className={styles.closeRow}>
      <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close company picker"><LuX /></button>
    </div>
    <div className={styles.picker}>
      <div className={styles.accountCard}>
        <div className={styles.accountHeader}>
          <button type="button" className={styles.accountToggle} disabled={!canExpand}
            aria-expanded={expanded} aria-controls="company-options" onClick={() => setExpanded(!expanded)}>
            <span className={styles.buildingIcon}><LuBuilding2 /></span>
            <span className={styles.selectedText}>
              <span className={styles.selectedName} title={selectedCompany?.company_name || "Select Company"}>
                {selectedCompany?.company_name || "Select Company"}
              </span>
              <span className={styles.companyDetail}>
                {[selectedCompany?.industry, selectedCompany?.city].filter(Boolean).join(" • ") || "Active Workspace"}
              </span>
            </span>
          </button>
          <div className={styles.selectedActions}>
            {defaultControl(selectedCompany, isSameSelectedAndDefault)}
            {canExpand && <button type="button" className={styles.chevron} aria-label={expanded ? "Collapse companies" : "Expand companies"}
              aria-expanded={expanded} aria-controls="company-options" onClick={() => setExpanded(!expanded)}>
              <LuChevronDown className={expanded ? styles.rotated : ""} />
            </button>}
          </div>
        </div>
        {canExpand && <div id="company-options" className={styles.options} hidden={!expanded}>
          {(companies.length > 3 || searchTerm || search) && <div className={styles.search}>
            <LuSearch aria-hidden="true" />
            <input type="text" aria-label="Search other companies" placeholder="Search other companies..."
              value={searchTerm} onChange={(event) => { setSearchTerm(event.target.value); setMessage(""); }} />
            {searchTerm && <button type="button" aria-label="Clear company search" onClick={() => { setSearchTerm(""); setSearch(""); }}><LuX /></button>}
          </div>}
          <div className={styles.companyList} ref={listRef} aria-busy={activeQuery.isFetching}>
            {activeQuery.isLoading ? <Spinner /> : <>
              {search ? searchQuery.companies.map(companyRow) : orderedCompanies.map(companyRow)}
              {!activeQuery.isError && (search ? searchQuery.companies : orderedCompanies).length === 0 &&
                <p className={styles.empty}>{search ? `No companies match "${searchTerm}"` : "No other companies available"}</p>}
            </>}
            {activeQuery.isError && <div className={styles.error} role="alert">
              <p>{requestError(activeQuery.error, "Could not load companies. Please try again.")}</p>
              <button type="button" onClick={() => activeQuery.isFetchNextPageError ? activeQuery.fetchNextPage() : activeQuery.refetch()}>Retry loading companies</button>
            </div>}
            <div ref={sentinelRef} className={styles.sentinel} />
            {activeQuery.isFetchingNextPage ? <Spinner label="Loading more companies" /> : hasNextPage && !activeQuery.isError &&
              <button type="button" className={styles.loadMore} onClick={() => fetchNextPage()}>Load more companies</button>}
          </div>
          {isAdmin && <div className={styles.createRow}>
            <a href={createCompanyUrl()} className={styles.createCompany}><span><LuPlus /></span>Create another company</a>
          </div>}
        </div>}
      </div>
      {mutation.isError && <div className={styles.error} role="alert">
        <p>{requestError(mutation.error, "Could not update the default company. Please try again.")}</p>
        <button type="button" disabled={mutation.isLoading} onClick={() => mutation.mutate(mutation.variables)}>Retry setting default</button>
      </div>}
      {message && <p className={styles.success} role="status">{message}</p>}
    </div>
  </>;
}

export default function ProfileDropdown({ onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const companyQuery = useUserCompanies();
  const { brandInfo } = useAppBrandInfo();

  const closeAndFocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector("button")?.focus();
    const outside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("touchstart", outside);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("touchstart", outside);
    };
  }, [open]);

  return <div className={styles.root} ref={rootRef}
    onKeyDown={(event) => { if (event.key === "Escape" && open) { event.stopPropagation(); closeAndFocus(); } }}
    onBlur={(event) => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <button type="button" className={styles.trigger} ref={triggerRef} aria-label="User Profile and Companies"
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? "company-picker" : undefined}
      onClick={() => setOpen(!open)}>
      {companyQuery.selectedCompany?.company_name && <span title={companyQuery.selectedCompany.company_name}>{companyQuery.selectedCompany.company_name}</span>}
      <LuCircleUserRound aria-hidden="true" />
    </button>
    {open && <div id="company-picker" role="dialog" aria-label="User Profile and Companies" className={styles.panel} ref={panelRef}>
      <div className={styles.panelContent}><CompanyPicker companyQuery={companyQuery} onClose={closeAndFocus} /></div>
      <div className={styles.accountLinks}>
        <button type="button" onClick={onLogout}><LuLogOut aria-hidden="true" /><span>Sign out</span></button>
      </div>
      {brandInfo.product_version && <div className={styles.versionFooter}>
        <span>{brandInfo.app_name} {brandInfo.product_name}</span><code>{brandInfo.product_version}</code>
      </div>}
    </div>}
  </div>;
}
